import React from 'react';
import { useTheme, Box, Typography, CircularProgress } from '@mui/material';
import { Handshake as HandshakeIcon } from '@mui/icons-material';
import { VehicleAutoNegoSession, AutoNegoState } from '../../models/vehicleAutoNegoSession';
import { useTranslation } from 'react-i18next';
import { getAutoNegoStatusColor, getAutoNegoStatusLabel } from '../../services/autoSessionService';
import BaseBadge from './BaseBadge';

interface AutoNegoBadgeProps {
  session: VehicleAutoNegoSession | null;
  loading?: boolean;
  onClick?: () => void;
  size?: 'small' | 'medium';
  showLabel?: boolean;
}

const AutoNegoBadge: React.FC<AutoNegoBadgeProps> = ({ 
  session, 
  loading, 
  onClick, 
  size = 'small',
  showLabel = false 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const isNegoOn = session?.autoNegoState === AutoNegoState.ON;
  const statusColor = getAutoNegoStatusColor(theme, session);
  const statusLabel = getAutoNegoStatusLabel(t, session, loading);

  if (showLabel) {
    const getStatusText = () => {
      if (loading) return t('Loading...');
      if (!session) return t('OFF');
      switch (session.autoNegoState) {
        case AutoNegoState.ON:
          return t('Active');
        case AutoNegoState.WAITING_RESULTS:
          return t('Waiting for Results');
        case AutoNegoState.RESULT_TO_REVIEW:
          return t('Results to Review');
        case AutoNegoState.COMPLETED:
          return t('Completed');
        case AutoNegoState.ERRORED:
          return t('Error');
        case AutoNegoState.OFF:
        default:
          return t('Inactive');
      }
    };

    return (
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 1,
          borderRadius: 2,
          border: `1px solid ${isNegoOn ? theme.palette.border.green : theme.palette.border.medium}`,
          backgroundColor: isNegoOn 
            ? 'rgba(34, 197, 94, 0.05)' 
            : theme.palette.mode === 'dark' 
              ? 'transparent' 
              : 'background.white',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          '&:hover': onClick ? {
            borderColor: isNegoOn ? theme.palette.border.green : theme.palette.border.medium,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            transform: 'translateY(-1px)',
          } : {},
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: statusColor,
            transition: 'color 0.2s ease',
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: 'inherit' }} />
          ) : (
            <HandshakeIcon sx={{ fontSize: 20 }} />
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'text.dark',
              fontSize: '0.8125rem',
              mb: 0.25,
            }}
          >
            {t('Auto Negotiation')}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: statusColor,
              fontSize: '0.75rem',
              fontWeight: 500,
            }}
          >
            {getStatusText()}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <BaseBadge
      icon={<HandshakeIcon />}
      loading={loading}
      onClick={onClick}
      size={size}
      tooltipTitle={statusLabel}
      statusColor={statusColor}
      isActive={isNegoOn}
      activeBackgroundColor="action.hover"
      activeHoverColor="border.green"
    />
  );
};

export default AutoNegoBadge;
